import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search, ChevronDown, ChevronRight, BookOpen,
  CalendarDays, BarChart3, Users, FileText, Zap, Shield,
  Bell, Settings, Smartphone, Phone, TrendingUp, Clock,
  CheckCircle2, Target, MessageSquare, RefreshCw
} from 'lucide-react';

const SECTIONS = [
  {
    id: 'getting-started', title: 'Getting Started', icon: BookOpen,
    content: [
      { q: 'Logging In', a: 'Open OpsFlow in your browser or tap the app icon. Enter your email and password, then click Sign In. If you\'ve forgotten your password, click "Forgot Password" to receive a reset email.' },
      { q: 'Installing on iPhone/iPad', a: 'Open OpsFlow in Safari → tap the Share button (square with arrow) → tap "Add to Home Screen". The app installs with the OpsFlow icon.' },
      { q: 'Installing on Android', a: 'Open OpsFlow in Chrome. You\'ll see an Install prompt — tap it. Or tap the three-dot menu → Install app.' },
      { q: 'First-Time Setup (Director)', a: 'After first login: 1) Admin → Branding (upload logo, set colours), 2) Admin → Team (invite members), 3) Admin → Rules (set decision thresholds), 4) Admin → Routines (configure daily targets), 5) Admin → System (configure SMTP for emails).' },
    ]
  },
  {
    id: 'today', title: 'Today — Daily Command Centre', icon: Clock,
    content: [
      { q: 'What are the daily routine blocks?', a: 'Four blocks: Outreach (new contacts, cold calls — target 5/day), Follow-ups (chase quotes, meetings — target 3/day), Visibility (LinkedIn, networking — target 2/day), Pipeline Hygiene (update contacts, review leads — target 2/day).' },
      { q: 'How do Quick Actions work?', a: 'Tap a quick action button (e.g. "Make cold call") to instantly create a task for today. Complete tasks by tapping the checkbox. Progress fills toward your daily target. Directors can customise quick actions in Admin → Routines.' },
      { q: 'How do I set reminders?', a: 'Click the + button in the Reminders section, enter a title and time. Reminders appear as notifications when due.' },
    ]
  },
  {
    id: 'enquiries', title: 'Enquiries — Project Intake', icon: FileText,
    content: [
      { q: 'How do I create an enquiry?', a: 'Click "+ New Enquiry" to start the wizard: Sector → Project Type → Client Details → Client Type → Project Details → Clarity → Value → Review & Submit.' },
      { q: 'Can I link to existing contacts?', a: 'Yes. In step 3 (Client Details), select "Existing Contact" and search or scroll through your contacts. They\'re sorted A-Z.' },
      { q: 'What is the Residential Sub-Wizard?', a: 'When you select a residential sector, additional criteria appear (construction value threshold, fee threshold, minimum criteria to pass). These thresholds are configurable by the Director in Admin → Rules.' },
    ]
  },
  {
    id: 'decisions', title: 'Decision Engine — Quote or Don\'t Quote', icon: Target,
    content: [
      { q: 'How does the Decision Engine work?', a: 'Every enquiry must go through the Decision Engine before a quote can be created. It evaluates against your team\'s rules: minimum construction value, minimum fee, risk assessment. Outcome: Quote, Don\'t Quote, or Review Required.' },
      { q: 'Can a Director override "Don\'t Quote"?', a: 'Yes. Directors can override a "Don\'t Quote" decision and create a quote anyway. Non-directors cannot.' },
      { q: 'Where are the rules configured?', a: 'Admin → Rules tab. Set minimum construction value, minimum fee value, and whether HIGH risk blocks quote creation.' },
    ]
  },
  {
    id: 'quotes', title: 'Quotes — Fee Proposals', icon: FileText,
    content: [
      { q: 'How do I create a quote?', a: 'From an enquiry with a "Quote" decision → click Create Quote. Add line items (description, discipline, quantity, unit price). VAT is calculated automatically.' },
      { q: 'What is the approval workflow?', a: 'Quotes are created as Draft → Submit for Director Approval → Director Approves or Rejects → Approved quotes can be sent to clients.' },
      { q: 'How does sending to client work?', a: 'Click "Send to Client" on an approved quote. Edit the email message, then Send. The client receives a branded email with the PDF and two buttons: APPROVED and NOT APPROVED. When clicked, OpsFlow is automatically notified.' },
      { q: 'What are Next Actions?', a: 'When a quote is created, 6 mandatory tasks are auto-generated: review line items, verify scope, check fees, confirm client details, prepare documents, submit for approval.' },
      { q: 'How do I download the PDF?', a: 'Click "Download PDF" on the quote page. It generates a branded document with your company logo, client details, line items, totals, terms, and footer.' },
    ]
  },
  {
    id: 'contacts', title: 'Pipeline & Contacts', icon: Users,
    content: [
      { q: 'How do I add a contact?', a: 'Go to Contacts → Add Contact. Fill in name, company, email, phone, position, sector, LinkedIn URL, company website, and Lead Source.' },
      { q: 'What is a Lead Source?', a: 'How you found the contact: Referral, LinkedIn, Cold Outreach, Networking, or Dormant. This auto-assigns a Scenario Profile which determines the recommended follow-up sequence.' },
      { q: 'What are Scenario Profiles?', a: 'Referred Prospect (W4, warmest), Networking Contact (W3), New LinkedIn Connection (W2), Dormant Re-engagement (W2), Cold Prospect (W1, coldest). Each has a different outreach cadence.' },
      { q: 'How do I log outreach?', a: 'Click a contact → Log Outreach. Select type (Call, Email, Meeting, LinkedIn), record outcome and notes. Follow-up date is suggested automatically.' },
      { q: 'What does "Mark as Engaged" do?', a: 'Stops all active sequences for that contact and marks them as engaged. Use when a contact responds positively outside of the normal sequence flow.' },
    ]
  },
  {
    id: 'sequences', title: 'Sequences — Automated Follow-ups', icon: Zap,
    content: [
      { q: 'What are sequences?', a: 'Pre-built series of follow-up tasks that execute over days/weeks. They standardise outreach and ensure no contact falls through the cracks.' },
      { q: 'How do I enrol a contact?', a: 'Open a contact → you\'ll see "Recommended Sequence" with an "Enrol Now" button. Click it. Tasks are created automatically as each step becomes due.' },
      { q: 'How do I process due steps?', a: 'Go to Sequences page → click "Process Due Steps" (Director only). This creates today\'s tasks for all enrollments where the next step is due.' },
      { q: 'Can I advance steps manually?', a: 'Yes. On the Active Enrollments section, click the "Next" button to skip to the next step immediately.' },
      { q: 'When do sequences auto-stop?', a: 'When engagement is detected: meeting booked, call connected, email responded, or you click "Mark as Engaged" on the contact.' },
      { q: 'What is the Scenario Performance dashboard?', a: 'Shows metrics per profile: enrollments, engagement rate, meetings, avg days to engagement. Click the info icon (i) to see the full sequence steps.' },
    ]
  },
  {
    id: 'calendar', title: 'Calendar', icon: CalendarDays,
    content: [
      { q: 'How do I create a calendar event?', a: 'Click a date on the calendar → fill in title, description, date, time, duration, category.' },
      { q: 'How do I sync with my phone calendar?', a: 'Go to Calendar → click the link icon (Export/Sync) → Subscribe tab → copy the URL → paste into your calendar app (Google, Outlook, Apple).' },
      { q: 'Can I edit events?', a: 'You can edit your own tasks. Directors can edit all tasks. On desktop, drag-and-drop to reschedule.' },
      { q: 'How do I set reminders for events?', a: 'Click "Alerts" on the Calendar page to choose which event types trigger reminders and set timing.' },
    ]
  },
  {
    id: 'dashboard', title: 'Dashboard & Analytics', icon: BarChart3,
    content: [
      { q: 'What does the Dashboard show?', a: 'KPI cards (enquiries, quotes, pipeline value, win rate), recent activity, team performance (Director only), and upcoming schedule widget (next 7 days of events).' },
      { q: 'What is Lead Source Performance?', a: 'On the Analytics page: revenue, conversion rate, and pipeline value broken down by scenario profile. Shows which lead sources produce the best results.' },
      { q: 'How are reports generated?', a: 'Go to Reports → select date range → Generate. Download as a branded PDF with KPIs, activity chart, and team performance.' },
    ]
  },
  {
    id: 'scripts', title: 'Script Library', icon: MessageSquare,
    content: [
      { q: 'What are scripts?', a: 'Pre-written templates for cold calls, follow-ups, objection handling, and closing conversations.' },
      { q: 'How do I personalise a script?', a: 'Click Personalise → select "Fill from Contact" to auto-fill the template with a contact\'s name, company, and location. Edit any variable, then Copy to clipboard.' },
    ]
  },
  {
    id: 'notifications', title: 'Notifications', icon: Bell,
    content: [
      { q: 'What gets notified?', a: 'Approval requests/decisions, quote wins/losses, tasks due today, follow-up reminders, sequence steps due, calendar events, client quote responses.' },
      { q: 'How do I enable push notifications?', a: 'My Profile → Push Notifications → Enable. Grant browser permission. Click Send Test to verify.' },
      { q: 'How do I clear notifications?', a: 'Click the X on individual notifications to permanently delete them, or click "Clear All" to remove everything.' },
    ]
  },
  {
    id: 'admin', title: 'Admin Settings (Director Only)', icon: Settings,
    content: [
      { q: 'Team', a: 'View members, invite new ones, change roles, deactivate users.' },
      { q: 'Branding', a: 'Upload company logo and set brand colours. These appear on all PDF exports.' },
      { q: 'Rules', a: 'Configure Decision Engine thresholds (min construction value, min fee, high risk blocks). Set Residential Enquiry Wizard thresholds.' },
      { q: 'Strengths', a: 'Enable/disable MEP sectors: Commercial, Residential, Transport, Healthcare, Education, Data Centers, Hospitality.' },
      { q: 'Routines', a: 'Configure daily blocks: targets, duration, schedule time, active days, and Quick Actions (add/edit/delete).' },
      { q: 'Digest', a: 'Configure weekly email digest: enable, day/time, recipients. Send Test to verify. Clear History to remove old logs.' },
      { q: 'Calendar', a: 'Calendar Health dashboard, subscription management, Clear Revoked subscriptions.' },
      { q: 'System', a: 'Build info, SMTP email configuration, real-time counts (users, contacts, enquiries, quotes, outreach, sequences).' },
    ]
  },
  {
    id: 'mobile', title: 'Mobile App', icon: Smartphone,
    content: [
      { q: 'How do I customise the bottom nav?', a: 'My Profile → Mobile Navigation. Choose up to 4 pages, click Save. Remaining pages are in the "More" menu.' },
      { q: 'Does it work in landscape?', a: 'Yes. Both phone and tablet support portrait and landscape rotation.' },
      { q: 'Can I import phone contacts?', a: 'Yes. Go to Contacts → Import to add contacts from your device\'s address book.' },
    ]
  },
  {
    id: 'roles', title: 'Roles & Permissions', icon: Shield,
    content: [
      { q: 'Director', a: 'Full access. Create/edit/delete everything. Approve quotes. Override "Don\'t Quote". Manage team. All Admin settings.' },
      { q: 'BD Manager', a: 'Manage contacts, pipeline, outreach. Create enquiries and quotes. Cannot approve quotes.' },
      { q: 'Senior Engineer', a: 'Create enquiries, manage assigned contacts. View pipeline. Limited editing.' },
      { q: 'Engineer', a: 'View-only on most modules. Can complete assigned tasks and log outreach.' },
    ]
  },
];

export default function HelpPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [expandedSection, setExpandedSection] = useState(null);

  const filtered = search.trim()
    ? SECTIONS.map(s => ({
        ...s,
        content: s.content.filter(c =>
          c.q.toLowerCase().includes(search.toLowerCase()) ||
          c.a.toLowerCase().includes(search.toLowerCase())
        )
      })).filter(s => s.content.length > 0)
    : SECTIONS;

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6" data-testid="help-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-[#1e3a5f]">
              User Guide
            </h1>
            <p className="text-slate-500 mt-1">Everything you need to know about OpsFlow</p>
          </div>
          <BookOpen className="w-10 h-10 text-[#1e3a5f]/20" />
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search the guide..."
            className="pl-10 rounded-xl"
            data-testid="help-search"
          />
        </div>

        {/* Sections */}
        <div className="space-y-3">
          {filtered.map((section) => {
            const Icon = section.icon;
            const isExpanded = expandedSection === section.id || search.trim().length > 0;

            return (
              <div key={section.id} className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                <button
                  onClick={() => setExpandedSection(isExpanded && !search ? null : section.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors text-left"
                  data-testid={`help-section-${section.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#1e3a5f]" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-slate-800">{section.title}</h2>
                      <p className="text-xs text-slate-500">{section.content.length} topics</p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3">
                    {section.content.map((item, idx) => (
                      <div key={idx} className="p-4 bg-slate-50 rounded-xl">
                        <h3 className="font-medium text-sm text-[#1e3a5f] mb-2">{item.q}</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">{item.a}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No results found for "{search}"</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
